import { BellIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useNotifications } from "@/context/notifications";
import * as analytics from "@/lib/analytics";
import { cn } from "@/lib/utils";
import { Button } from "./ui/button";

const stateKey = "helios:notifications_banner_dismissed";
const stateValue = "true";

/**
 * Floating prompt asking the user to enable push notifications. Follows the
 * feature-announcement visual language: card chrome, quiet entrance, and a
 * "Not now" / primary action footer. Sits bottom-right on desktop and above
 * the tab bar on mobile so it never covers app chrome.
 */
export function NotificationsPrompt() {
  const { isSubscribed, subscribe } = useNotifications();
  const [isDismissed, setIsDismissed] = useState(true);

  useEffect(() => {
    setIsDismissed(localStorage.getItem(stateKey) === stateValue);
  }, []);

  // `isSubscribed` is null while the subscription state is still loading.
  const visible = !isDismissed && isSubscribed === false;

  useEffect(() => {
    if (visible) analytics.track("Notification Banner Viewed");
  }, [visible]);

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem(stateKey, stateValue);
    analytics.track("Notification Banner Dismissed");
  };

  const handleEnable = async () => {
    await subscribe();
    analytics.track("Notification Banner Enabled");
  };

  if (!visible) return null;

  return (
    <section
      role="region"
      aria-label="Enable notifications"
      className={cn(
        "fixed z-50 rounded-xl border border-border bg-card p-4 shadow-lg",
        "inset-x-4 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] sm:inset-x-auto sm:bottom-4 sm:right-4 sm:w-full sm:max-w-sm",
        "animate-in fade-in-0 slide-in-from-bottom-2 duration-300 motion-reduce:animate-none",
      )}
    >
      <div className="flex items-start gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
          <BellIcon className="size-4" />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-foreground">Turn on notifications</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Get a heads-up about new leads and messages, even when Helios is in the background.
          </p>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={handleDismiss}>
          Not now
        </Button>
        <Button size="sm" onClick={handleEnable}>
          Enable notifications
        </Button>
      </div>
    </section>
  );
}
